// Service for managing saved jobs
import { Job } from '../types/JobTypes';
import { savedJobsAPI } from './api/savedJobs.api';

export interface SavedJob extends Job {
  savedAt: Date;
  notes?: string;
  applied?: boolean;
  appliedAt?: Date;
  status?: 'active' | 'expired' | 'applied' | 'archived';
}

export interface JobApplication {
  applicationId: string;
  jobId: string;
  savedJobId: string;
  appliedAt: Date;
  resumeVersion?: string;
  coverLetterIncluded: boolean;
  status: 'submitted' | 'under_review' | 'shortlisted' | 'interview_scheduled' | 
          'interviewed' | 'offer_received' | 'rejected' | 'withdrawn' | 'accepted';
  statusUpdatedAt: Date;
  interviewDate?: Date;
  offerDate?: Date;
  userNotes?: string;
  companyFeedback?: string;
  offeredSalary?: number;
  negotiatedSalary?: number;
}

class SavedJobsService {
  private baseUrl = '/api/saved-jobs';
  private mockSavedJobs: Map<string, SavedJob[]> = new Map(); // Mock storage for development

  // Save a job
  async saveJob(userId: string, job: Job, notes?: string): Promise<SavedJob> {
    try {
      // Use backend API if available and not default localhost
      if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
        return await savedJobsAPI.saveJob(userId, job, notes);
      }
      
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        // Load existing jobs from localStorage
        const stored = localStorage.getItem(`savedJobs_${userId}`);
        const userJobs: SavedJob[] = stored ? JSON.parse(stored) : [];
        
        // Check if job already exists
        const existingJobIndex = userJobs.findIndex(j => j.id === job.id);
        if (existingJobIndex !== -1) {
          // Update existing job
          userJobs[existingJobIndex] = {
            ...userJobs[existingJobIndex],
            ...job,
            notes: notes || userJobs[existingJobIndex].notes,
            savedAt: userJobs[existingJobIndex].savedAt // Keep original save date
          };
        } else {
          // Add new job
          const savedJob: SavedJob = {
            ...job,
            savedAt: new Date(),
            notes,
            applied: false,
            status: 'active'
          };
          userJobs.push(savedJob);
        }
        
        this.mockSavedJobs.set(userId, userJobs);
        
        // Store in localStorage for persistence
        localStorage.setItem(`savedJobs_${userId}`, JSON.stringify(userJobs));
        
        return userJobs.find(j => j.id === job.id)!;
      }
      
      const response = await fetch(`${this.baseUrl}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          job,
          notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save job');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error saving job:', error);
      throw error;
    }
  }

  // Get user's saved jobs
  async getSavedJobs(userId: string, filters?: {
    status?: string;
    applied?: boolean;
    industry?: string;
    location?: string;
  }): Promise<SavedJob[]> {
    try {
      // Use backend API if available and not default localhost
      if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
        return await savedJobsAPI.getSavedJobs(userId);
      }
      
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        // Load from localStorage
        const stored = localStorage.getItem(`savedJobs_${userId}`);
        const jobs = stored ? JSON.parse(stored) : [];
        this.mockSavedJobs.set(userId, jobs);
        
        // Apply filters if provided
        let filteredJobs = jobs;
        if (filters) {
          if (filters.status) {
            filteredJobs = filteredJobs.filter((job: SavedJob) => job.status === filters.status);
          }
          if (filters.applied !== undefined) {
            filteredJobs = filteredJobs.filter((job: SavedJob) => job.applied === filters.applied);
          }
          if (filters.industry) {
            filteredJobs = filteredJobs.filter((job: SavedJob) => job.industry === filters.industry);
          }
          if (filters.location) {
            filteredJobs = filteredJobs.filter((job: SavedJob) => job.location.includes(filters.location!));
          }
        }
        
        return filteredJobs;
      }
      
      const queryParams = new URLSearchParams({ userId });
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await fetch(`${this.baseUrl}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved jobs');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching saved jobs:', error);
      throw error;
    }
  }

  // Remove a saved job
  async removeSavedJob(userId: string, jobId: string): Promise<void> {
    try {
      // Use backend API if available and not default localhost
      if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
        return await savedJobsAPI.removeSavedJob(userId, jobId);
      }
      
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const userJobs = this.mockSavedJobs.get(userId) || [];
        const filteredJobs = userJobs.filter(job => job.id !== jobId);
        this.mockSavedJobs.set(userId, filteredJobs);
        
        // Update localStorage
        localStorage.setItem(`savedJobs_${userId}`, JSON.stringify(filteredJobs));
        
        return;
      }
      
      const response = await fetch(`${this.baseUrl}/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        throw new Error('Failed to remove saved job');
      }
    } catch (error) {
      // console.error('Error removing saved job:', error);
      throw error;
    }
  }

  // Update saved job (e.g., add notes, mark as applied)
  async updateSavedJob(
    userId: string, 
    jobId: string, 
    updates: Partial<SavedJob>
  ): Promise<SavedJob> {
    try {
      const response = await fetch(`${this.baseUrl}/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          updates
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update saved job');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error updating saved job:', error);
      throw error;
    }
  }

  // Apply to a job
  async applyToJob(
    userId: string, 
    jobId: string, 
    applicationData: {
      resumeVersion?: string;
      coverLetterIncluded?: boolean;
      notes?: string;
    }
  ): Promise<JobApplication> {
    try {
      const response = await fetch(`${this.baseUrl}/${jobId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          ...applicationData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to apply to job');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error applying to job:', error);
      throw error;
    }
  }

  // Get user's job applications
  async getApplications(userId: string): Promise<JobApplication[]> {
    try {
      const response = await fetch(`${this.baseUrl}/applications?userId=${userId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching applications:', error);
      throw error;
    }
  }

  // Update application status
  async updateApplicationStatus(
    userId: string,
    applicationId: string,
    status: JobApplication['status'],
    additionalData?: {
      interviewDate?: Date;
      offerDate?: Date;
      offeredSalary?: number;
      companyFeedback?: string;
      userNotes?: string;
    }
  ): Promise<JobApplication> {
    try {
      const response = await fetch(`${this.baseUrl}/applications/${applicationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          status,
          ...additionalData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update application status');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error updating application status:', error);
      throw error;
    }
  }

  // Get saved jobs statistics
  async getSavedJobsStats(userId: string): Promise<{
    totalSaved: number;
    activeSaved: number;
    totalApplied: number;
    expiredJobs: number;
    avgSavedSalary: number;
    uniqueCompanies: number;
    uniqueCities: number;
    lastSavedAt: Date;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/stats?userId=${userId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved jobs statistics');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching saved jobs statistics:', error);
      throw error;
    }
  }

  // Helper method to get auth token
  private getAuthToken(): string {
    // This should be implemented based on your auth system
    return localStorage.getItem('authToken') || '';
  }
}

export const savedJobsService = new SavedJobsService();